import React from "react";
import { Inbox } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";
import { Colors } from "../../utils/utils.colors";

const STATUS_LABELS: Record<string, string> = {
  published: "Publié",
  pending: "En attente",
  rejected: "Rejeté",
  draft: "Brouillon",
};

export const AdminPageLoader: React.FC<{ label?: string }> = ({ label }) => {
  const { t } = useLanguage();
  return (
    <div className="admin-loader" data-testid="admin-page-loader">
      <div className="admin-loader__spinner" />
      <span className="admin-loader__text">{label || t.common.loading}</span>
    </div>
  );
};

export const AdminSkeletonStats: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="admin-stats-grid" data-testid="admin-skeleton-stats">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="admin-skeleton admin-skeleton-stat" />
    ))}
  </div>
);

export const AdminSkeletonRows: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div data-testid="admin-skeleton-rows">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="admin-skeleton admin-skeleton-row" />
    ))}
  </div>
);

export const AdminTableSkeleton: React.FC = () => (
  <Card>
    <CardContent className="p-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="admin-skeleton admin-skeleton-row mb-2" style={{ height: i === 0 ? 36 : 52 }} />
      ))}
    </CardContent>
  </Card>
);

export const AdminLoadingView: React.FC<{ slug: string; isAdmin?: boolean }> = ({ slug, isAdmin }) => {
  if (slug === "overview" || slug === "") {
    return (
      <>
        <AdminSkeletonStats count={isAdmin ? 10 : 5} />
        <Card className="mt-6"><CardContent><AdminSkeletonRows count={6} /></CardContent></Card>
      </>
    );
  }
  if (slug === "users") return <AdminTableSkeleton />;
  return <AdminPageLoader />;
};

export const AdminPageHeader: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}> = ({ title, subtitle, action }) => (
  <div className="admin-page-header">
    <div>
      <h2 className="!text-xl !font-bold !text-[#041797] !mb-0">{title}</h2>
      {subtitle && <p className="!mt-1">{subtitle}</p>}
    </div>
    <div className="div">
      {action}
    </div>
  </div>
);

export const AdminStatCard: React.FC<{
  label: string;
  value: number | string;
  color?: string;
  Icon?: React.ComponentType<{ size?: number; color?: string }>;
}> = ({ label, value, color = "#041797", Icon }) => (
  <div className="admin-stat-card">
    {Icon && (
      <div className="admin-stat-card__icon" style={{ background: `${color}14`, color }}>
        <Icon size={18} color={color} />
      </div>
    )}
    <div className="admin-stat-card__value">{value ?? 0}</div>
    <div className="admin-stat-card__label">{label}</div>
  </div>
);

export const AdminStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const key = STATUS_LABELS[status] ? status : "draft";
  return <span className={`admin-badge admin-badge--${key}`}>{STATUS_LABELS[key] || status}</span>;
};

export const AdminEmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
      <Inbox className="h-6 w-6" color={Colors.primaryColor} />
    </div>
    <p className="text-sm text-muted-foreground max-w-md">{message}</p>
  </div>
);

export const AdminBtnPrimary: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }> = ({ children, className = "", ...props }) => (
  <Button className={cn("shadow-md", className)} {...props}>{children}</Button>
);

export const AdminBtnPrimaryRefresh: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }> = ({ children, className = "", ...props }) => (
  <Button className={cn("shadow-md", className)} {...props}>{children}</Button>
);

export const AdminBtnIcon: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "edit" | "delete" | "success" | "danger";
    children: React.ReactNode;
  }
> = ({ variant = "edit", children, className = "", ...props }) => {
  const mod =
    variant === "success" ? "admin-btn--icon--success"
      : variant === "danger" || variant === "delete" ? "admin-btn--icon--danger"
        : "admin-btn--icon--edit";
  return (
    <button type="button" className={`admin-btn admin-btn--icon ${mod} ${className}`.trim()} {...props}>
      {children}
    </button>
  );
};

export const AdminPanel: React.FC<{ title?: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <Card className={cn("overflow-hidden", className)}>
    {title?.trim() && <div className="border-b border-border px-5 py-4 text-sm font-semibold text-foreground">{title}</div>}
    <CardContent>{children}</CardContent>
  </Card>
);

export const AdminCrudPage: React.FC<{
  title: string;
  subtitle?: string;
  emptyMessage: string;
  isEmpty: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, emptyMessage, isEmpty, action, children }) => (
  <>
    <AdminPageHeader title={title} subtitle={subtitle} action={action} />
    <AdminPanel>{isEmpty ? <AdminEmptyState message={emptyMessage} /> : children}</AdminPanel>
  </>
);

export const AdminDataRow: React.FC<{
  testId?: string;
  thumb?: string;
  children: React.ReactNode;
  actions: React.ReactNode;
}> = ({ testId, thumb, children, actions }) => (
  <div className="admin-data-row" data-testid={testId}>
    {thumb && <img src={thumb} alt="" className="admin-data-row__thumb" />}
    <div className="admin-data-row__body">{children}</div>
    <div className="admin-data-row__actions">{actions}</div>
  </div>
);
